import { useState } from "react";

const FORM_ADDR = "https://readdy.ai/api/form/daeo5ihtto0ceqqnh17g";

interface SubscribeFormProps {
  id: string;
  variant: "row" | "block";
  buttonText?: string;
  placeholder?: string;
}

export default function SubscribeForm({
  id,
  variant,
  buttonText = "立即订阅",
  placeholder = "输入常用邮箱，例如 you@example.com",
}: SubscribeFormProps) {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = (fd.get("contact_alt") as string) || "";
    if (honeypot.trim() !== "") {
      setStatus("success");
      setMessage("订阅成功！首期瓜报正在赶往你的邮箱，记得留意收件箱。");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    const email = (fd.get("email") as string) || "";
    if (email.trim() !== "") {
      payload.append("email", email.trim());
    }

    setStatus("loading");
    setMessage("");

    try {
      const res = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.meta?.detail ||
        parsed?.message ||
        (parsed?.code ? "" : responseText);
      const isSpam = /spam|form data is spam/i.test(serverMsg || responseText);
      const ok = res.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        setMessage("订阅成功！首期瓜报正在赶往你的邮箱，记得留意收件箱。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setMessage("网络似乎开小差了，请稍后再试一次。");
    }
  };

  return (
    <div className="w-full">
      <form
        id={id}
        data-readdy-form={id}
        onSubmit={handleSubmit}
        noValidate={false}
        className={variant === "row" ? "flex flex-col sm:flex-row gap-3" : "flex flex-col gap-3"}
      >
        <label htmlFor={`${id}-email`} className="sr-only">
          电子邮箱
        </label>
        <input
          id={`${id}-email`}
          name="email"
          type="email"
          required
          placeholder={placeholder}
          autoComplete="email"
          className="w-full text-sm rounded-md px-4 py-3 bg-background-50 text-foreground-900 placeholder:text-foreground-400 border border-background-300 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition"
        />
        {/* 反垃圾隐藏字段 */}
        <div className="hp-wrap" aria-hidden="true">
          <label htmlFor={`${id}-alt`}>contact_alt</label>
          <input
            id={`${id}-alt`}
            type="text"
            name="contact_alt"
            tabIndex={-1}
            autoComplete="off"
            readOnly
          />
        </div>
        <button
          type="submit"
          disabled={status === "loading"}
          className="whitespace-nowrap rounded-md bg-primary-500 text-white text-sm font-semibold px-6 py-3 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-400 transition disabled:opacity-70"
        >
          {status === "loading" ? "提交中…" : buttonText}
        </button>
      </form>

      <div className="mt-3 min-h-[1.25rem] text-sm">
        {status === "success" && (
          <p className="flex items-center gap-2 text-accent-700">
            <i className="ri-checkbox-circle-line text-lg"></i>
            <span>{message}</span>
          </p>
        )}
        {status === "error" && (
          <p className="flex items-center gap-2 text-primary-700">
            <i className="ri-error-warning-line text-lg"></i>
            <span>{message}</span>
          </p>
        )}
      </div>
    </div>
  );
}